import * as React from 'react';

import { ApolloError, ObservableQueryFields } from '@apollo/client';

import {
  GetReviewsQuery,
  GetReviewsQueryVariables,
} from '@notino/shared/definitions/types';

import ErrorBoundary from '@components/ErrorBoundary';

import { PAGE_SIZE } from '../constants';

import { FetchBlockFailed } from './FetchBlockFailed';
import { FetchDoneBlock } from './FetchDoneBlock';
import { FetchingBlock } from './FetchingBlock';
import { SortFilterBar } from './SortFilterBar';
import { StyledRow } from './styled';

interface IReviewsItemsProps {
  productReviewCount: number;
  isAllReviewsPage?: boolean;
  refetch: () => void;
  fetchMore: ObservableQueryFields<
    GetReviewsQuery,
    GetReviewsQueryVariables
  >['fetchMore'];
  reviewsAndFilters: GetReviewsQuery['reviewsAndFilters'];
  productCode: string;
  reviewsLoading: boolean;
  reviewsError: ApolloError;
}

export const ReviewsItems: React.FC<IReviewsItemsProps> = ({
  refetch,
  fetchMore,
  reviewsAndFilters,
  reviewsLoading,
  reviewsError,
  productCode,
  productReviewCount,
  isAllReviewsPage,
}) => {
  const lastReview = React.useRef(0);

  const { data } = reviewsAndFilters || {};

  if (!reviewsLoading && (data?.reviews?.length ?? 0) > 0) {
    lastReview.current = data?.reviews?.length ?? 0;
  }

  return (
    <StyledRow>
      {(data?.reviews || lastReview.current > 0) && (
        <>
          <SortFilterBar
            productReviewCount={productReviewCount}
            reviewsAndFilters={reviewsAndFilters}
          />

          {data?.reviews && (
            <ErrorBoundary>
              <FetchDoneBlock
                productReviewCount={productReviewCount}
                productCode={productCode}
                reviewsData={reviewsAndFilters?.data ?? null}
                loadReviews={fetchMore}
                isAllReviewsPage={isAllReviewsPage}
              />
            </ErrorBoundary>
          )}
        </>
      )}

      {data?.reviews.length === 0 && reviewsLoading && (
        <FetchingBlock reviewsCount={Math.min(productReviewCount, PAGE_SIZE)} />
      )}

      {data?.reviews.length === 0 && reviewsError && (
        <FetchBlockFailed loadReviews={refetch} />
      )}
    </StyledRow>
  );
};
